import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, Eye, EyeOff, AlertCircle, ArrowRight, KeyRound } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const AdminLogin = () => {
  const { adminKeyLogin } = useAuth();
  const nav = useNavigate();
  const [key, setKey] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      const u = await adminKeyLogin(key.trim());
      if (u.role !== "admin" && u.role !== "super_admin") {
        setErr("This key does not grant admin access.");
        setLoading(false);
        return;
      }
      nav("/admin", { replace: true });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#07080d] text-white flex items-center justify-center px-4 py-10 relative overflow-hidden">
      <div className="absolute inset-0 grid-pattern opacity-30 pointer-events-none"></div>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[500px] bg-gradient-to-br from-[#ff7a1a]/15 to-transparent blur-3xl pointer-events-none"></div>

      <div className="relative w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#ff7a1a] to-[#c2410c] flex items-center justify-center">
            <Shield size={18} className="text-white" />
          </div>
          <span className="font-display font-bold text-2xl tracking-tight">Admin Console</span>
        </div>

        <div className="bb-card rounded-3xl p-7 lg:p-8 border-[#ff7a1a]/15">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 mb-4 rounded-md bg-[#ff7a1a]/15 border border-[#ff7a1a]/30 text-[10px] uppercase tracking-wider font-semibold text-[#ff9a4a]">
            <Shield size={11} /> Restricted Area
          </div>
          <h1 className="font-display font-bold text-[24px] text-white mb-1">Enter access key</h1>
          <p className="text-[13px] text-white/55 mb-6">Admin access is granted via a secure access key. All actions are logged in the audit trail.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-[12px] text-white/55 mb-1.5 block">Access key</label>
              <div className="relative">
                <KeyRound size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/35" />
                <input
                  type={show ? "text" : "password"}
                  value={key}
                  onChange={(e) => setKey(e.target.value)}
                  required
                  autoFocus
                  autoComplete="off"
                  placeholder="Paste your admin access key"
                  data-testid="admin-key-input"
                  className="w-full h-11 pl-9 pr-10 bg-white/[0.02] border border-white/10 rounded-xl text-[14px] text-white outline-none focus:border-[#ff7a1a] placeholder:text-white/30 font-mono"
                />
                <button
                  type="button"
                  onClick={() => setShow(!show)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/45 hover:text-white"
                  tabIndex={-1}
                >
                  {show ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
            {err && (
              <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-rose-500/10 border border-rose-500/30 text-[12px] text-rose-300" data-testid="admin-login-error">
                <AlertCircle size={14} className="shrink-0 mt-0.5" />
                <span>{err}</span>
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full h-11 rounded-xl text-white font-medium text-[14px] inline-flex items-center justify-center gap-2 disabled:opacity-60"
              style={{ background: "linear-gradient(135deg,#ff7a1a,#c2410c)" }}
            >
              {loading ? "Verifying..." : (<>Unlock console <ArrowRight size={15} /></>)}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
